import { Link } from "react-router-dom";
import { Shield, FileText, Users, ClipboardList, ArrowRight, CheckCircle2, BookOpen, GraduationCap, Heart, FolderOpen, Calendar, BarChart, Mail, Globe } from "lucide-react";

const EDUCATION_IMG = "https://static.prod-images.emergentagent.com/jobs/38230353-f183-4be8-9a86-d08129efc3fa/images/b97fdc09c31fcd88e9b51be0ed48d525e07f129b78ebda7c46441934fad4ce7d.png";
const STUDENTS_IMG = "https://images.unsplash.com/photo-1758612898304-1a6bb546ac44?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxvbmxpbmUlMjBlZHVjYXRpb24lMjBzdHVkZW50cyUyMGxlYXJuaW5nfGVufDB8fHx8MTc3NjM1MjEwMXww&ixlib=rb-4.1.0&q=85";

const benefits = [
  {
    icon: Shield,
    title: "Complete School System",
    desc: "We build your entire church school platform — website, enrollment portal, admin dashboard, and student management.",
  },
  {
    icon: FileText,
    title: "Record Keeping",
    desc: "Maintain organized, thorough student records with systems designed specifically for church school administration.",
  },
  {
    icon: Users,
    title: "Enrollment Systems",
    desc: "Streamline student enrollment with clear processes that make it easy for families to join your program.",
  },
  {
    icon: ClipboardList,
    title: "Ongoing Support",
    desc: "Access ongoing technical support, system updates, and guidance to keep your school running smoothly.",
  },
];

const supportPoints = [
  "Professional church school system and technology",
  "Guidance on launching and operating your school",
  "Administrative systems and processes",
  "Ongoing technical support and consultation",
  "Association with ACS Virtual Church network",
];

export default function ChurchSchoolProgramPage() {
  return (
    <div data-testid="church-school-program-page">
      {/* Header */}
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
            Our Program
          </p>
          <h1
            data-testid="program-headline"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl"
          >
            Church School Program
          </h1>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Program Overview
            </p>
            <h2
              data-testid="program-overview-heading"
              className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-6"
            >
              Education Under a Church Covering
            </h2>
            <div className="space-y-4 text-[#595959] leading-relaxed">
              <p>
                Starting a church school requires the right technology and
                systems to manage enrollment, records, and daily operations.
                ACS Virtual Church builds complete church school platforms so
                your church can launch and operate its own independent school
                with confidence.
              </p>
              <p>
                We build the systems — you run your school. Each church school
                we create is an independent operation owned and managed by the
                church it belongs to. We handle the technology so you can focus
                on what matters most: education and faith.
              </p>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <img
              src={EDUCATION_IMG}
              alt="Faith and Education"
              className="w-full h-72 lg:h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="program-benefits-section" className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Benefits
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A]">
              What We Provide
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`benefit-card-${i}`}
                className="bg-white rounded-xl border border-[#E5E2DC] p-8 hover:shadow-md hover:-translate-y-1 transition-all"
              >
                <div className="w-12 h-12 rounded-lg bg-[#C8A97E]/10 flex items-center justify-center mb-5">
                  <b.icon className="w-5 h-5 text-[#C8A97E]" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-2">
                  {b.title}
                </h3>
                <p className="text-sm text-[#595959] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it supports */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="rounded-xl overflow-hidden shadow-lg order-2 lg:order-1">
            <img
              src={STUDENTS_IMG}
              alt="Online Learning"
              className="w-full h-72 lg:h-96 object-cover"
            />
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Support
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-6">
              How We Support Families and Churches
            </h2>
            <ul className="space-y-3">
              {supportPoints.map((point, i) => (
                <li
                  key={i}
                  data-testid={`support-point-${i}`}
                  className="flex items-start gap-3 text-[#595959]"
                >
                  <CheckCircle2 className="w-5 h-5 text-[#8F9779] shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/start-a-church-school"
              data-testid="program-cta-start"
              className="mt-8 inline-flex items-center gap-2 bg-[#1C2E4A] text-white hover:bg-[#152238] px-8 py-3 rounded-md transition-colors font-medium"
            >
              Start Your Own School <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Alabama Church School */}
      <section data-testid="cover-school-section" className="py-20 md:py-28 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">
              Our Church School
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#1C2E4A] mb-4">
              Alabama Church School
            </h2>
            <p className="text-base text-[#595959] leading-relaxed max-w-2xl mx-auto">
              A faith-based church school program providing a complete, supportive home-education experience for Alabama families. We handle the paperwork, curriculum, and recordkeeping so parents can focus on what matters most — guiding their children's hearts and minds.
            </p>
          </div>

          {/* What We Offer Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <BookOpen className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Christ-Centered Curriculum (K-12)</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Full curriculum across all major subjects, delivered through our parent portal. Lessons include parent guides and student instructions tailored to the grade level. Adjustable pacing — go faster, slower, or take a season off when life calls for it.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <GraduationCap className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Adult Diploma Program</h4>
              <p className="text-xs text-[#595959] leading-relaxed">A self-paced 6th-12th grade program for adults who want to finish their high school diploma. Built-in final exams and a real diploma upon completion.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <Heart className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Student Learning Plans</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Personalized learning support for students who need a little extra help. Parents tell us about their child's strengths, struggles, and goals; we build a simple plan with practical accommodations and lesson adjustments.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <Mail className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Automated Emails</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Enrollment confirmations, welcome letters, reminders, and notifications — all sent automatically to families.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <Globe className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Free Domain Name</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Your school gets its own custom domain name, included free for the first year.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <Shield className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">BOE & Driver's License Forms</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Auto-generated Alabama Board of Education enrollment letters and DL/learner's permit forms — exactly what you need to hand to the DMV or local district.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <FolderOpen className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Document Management</h4>
              <p className="text-xs text-[#595959] leading-relaxed">Upload and store previous school records, immunization forms, and family documents in one secure place.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <Calendar className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Calendar & Alerts</h4>
              <p className="text-xs text-[#595959] leading-relaxed">School calendar with important dates, plus instant alerts about events, holidays, and program updates.</p>
            </div>
            <div className="bg-white rounded-xl border border-[#E5E2DC] p-6">
              <BarChart className="w-5 h-5 text-[#C8A97E] mb-3" />
              <h4 className="font-serif text-base font-semibold text-[#1C2E4A] mb-1.5">Progress & Grade Tracking</h4>
              <p className="text-xs text-[#595959] leading-relaxed">See exactly what each student has completed, their grades, and where they are growing.</p>
            </div>
          </div>

          {/* How It Works */}
          <div className="max-w-2xl mx-auto mb-14">
            <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] text-center mb-6">How It Works</h3>
            <div className="space-y-4">
              {[
                "Enroll online — quick, secure, and you are set up the same day.",
                "Choose your subjects — full grade-level curriculum is unlocked instantly.",
                "Learn at your pace — open the parent portal anytime, from any device.",
                "Stay organized — enrollment records and all official paperwork handled in one system.",
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <span className="w-8 h-8 rounded-full bg-[#1C2E4A] text-white flex items-center justify-center text-sm font-semibold shrink-0">{i + 1}</span>
                  <p className="text-sm text-[#595959] leading-relaxed pt-1.5">{step}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div className="max-w-lg mx-auto bg-white rounded-xl border border-[#E5E2DC] p-8 text-center mb-14">
            <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-4">Pricing</h3>
            <div className="space-y-3 text-sm text-[#595959] mb-6">
              <div className="flex justify-between items-center border-b border-[#E5E2DC] pb-2">
                <span>New Family Enrollment</span>
                <span className="font-semibold text-[#1C2E4A]">$149/year per family</span>
              </div>
              <div className="flex justify-between items-center border-b border-[#E5E2DC] pb-2">
                <span>Re-enrollment</span>
                <span className="font-semibold text-[#1C2E4A]">$149/year per family</span>
              </div>
              <div className="flex justify-between items-center">
                <span>Adult Diploma Program</span>
                <span className="font-semibold text-[#1C2E4A]">$149/year</span>
              </div>
            </div>
            <p className="text-xs text-[#595959]/70 mb-6">No per-child fees. No add-child fees. No late fees. Pay by card, bank transfer (ACH), Cash App, or Zelle.</p>
            <a
              href="https://alabamachurchschool.com/enroll"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cover-school-enroll-link"
              className="inline-flex items-center gap-2 bg-[#C8A97E] text-white hover:bg-[#B3956A] px-8 py-3 rounded-md transition-colors font-medium"
            >
              Enroll at AlabamaChurchSchool.com <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {/* Why Families Choose ACS */}
          <div className="max-w-2xl mx-auto mb-10">
            <h3 className="font-serif text-xl font-semibold text-[#1C2E4A] text-center mb-6">Why Families Choose ACS</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                "Faith-based foundation rooted in our church community",
                "Flat family fee — no nickel-and-diming per child",
                "Administrative tools designed for Alabama church schools",
                "Live support from real people who know your family",
                "Modern parent portal, accessible 24/7",
                "No required testing, no rigid schedules",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-[#8F9779] shrink-0 mt-0.5" />
                  <span className="text-sm text-[#595959]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Disclaimer */}
          <p className="text-center text-xs text-[#595959]/50 italic max-w-2xl mx-auto">
            Alabama Church School is a private church school operating independently. We are not a state-issued IEP provider; our Student Learning Plans are internal academic-support documents only.
          </p>
        </div>
      </section>
    </div>
  );
}
